import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Button, IconButton, Typography, Box, Paper, CircularProgress } from '@mui/material';
import { ModalClose, WorkspacePremiumIcon } from '../assets/icons';
import { file, humanResource } from '../assets/images';
import ChargebeeService from '../services/chargebee.service';
import { toast } from 'react-toastify';
import StorageIcon from "@mui/icons-material/Storage";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";


interface BuyAddOnsProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    subscriptionId?: string;
    subscribedAddons?: string[];
    currentPlanId?: string;
}

interface AddonOption {
    id: string;
    title: string;
    description: string;
    price: number;
    icon: React.ReactNode;
}

const BuyAddOns: React.FC<BuyAddOnsProps> = ({ open, onClose, onConfirm, subscriptionId, subscribedAddons = [], currentPlanId = '' }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Determine if the current plan is monthly or yearly
    const isYearly = currentPlanId.toLowerCase().includes('yearly');
    const billingCycle = isYearly ? 'USD-Yearly' : 'USD-Monthly';

    // Define all available add-ons with base IDs
    const allAddons: AddonOption[] = [
        {
            id: 'White-Label-Branding',
            title: 'White labeling',
            description: 'Your Brand, Your Webinar: Remove Third-Party Logos, Customize the Experience, and Host on Your Own Domain with White Labeling!',
            price: isYearly ? 990 : 99,
            icon: <WorkspacePremiumIcon />
        },
        {
            id: 'One-Time-Webinar-Assist-and-Setup',
            title: 'One Time Webinar Assist & Setup',
            description: 'Get expert assistance in setting up your webinar from start to finish. Our team will help configure your settings, customize templates, and ensure everything runs smoothly.',
            price: 250,
            icon: <SupportAgentIcon />
        },
        {
            id: 'custom-domain',
            title: 'C-Name',
            description: 'Personalize your webinar experience with a custom domain that reflects your brand\'s identity.',
            price: isYearly ? 490 : 49,
            icon: <img src={file} alt="cname" />
        },
        {
            id: 'Storage-Add-on-5-GB',
            title: 'Additional Storage (5GB)',
            description: 'Expand your webinar storage capacity with additional space for recordings, presentations, and resources to ensure seamless content delivery.',
            price: isYearly ? 110 : 11,
            icon: <StorageIcon />
        }
    ];

    // Create full add-on IDs with billing cycle prefix
    const addonsWithFullIds = allAddons.map(addon => {
        // Skip adding billing cycle for one-time add-ons
        const fullId = addon.id === 'custom-domain'
            ? addon.id
            : `${addon.id}-${billingCycle}`;

        return {
            ...addon,
            id: fullId
        };
    });

    // Filter out already subscribed add-ons
    const availableAddons = addonsWithFullIds.filter(addon =>
        !subscribedAddons.some(subAddon => subAddon.includes(addon.id))
    );

    const handleAddToPlan = async (planId: string) => {
        if (!subscriptionId) {
            toast.error("No subscription found");
            return;
        }

        setIsProcessing(true);
        try {
            const response = await ChargebeeService.changePlan(
                subscriptionId,
                planId,
                1
            );

            // Check if the add-on was successfully added to the subscription
            const addOnExists = response?.subscription_items?.some(
                (item: { item_price_id: string; item_type: string }) =>
                    item.item_price_id === planId && item.item_type === "addon"
            );

            if (addOnExists) {
                toast.success(`Add-on added successfully to your subscription`);
                onConfirm(); // Call onConfirm to refresh parent component if needed
            } else {
                toast.success("Plan updated successfully");
            }
        } catch (error) {
            console.error("Error adding add-on:", error);
            toast.error("Failed to add add-on to your plan");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className='card-details-modal' sx={{ '& .MuiDialog-paper': { width: 'fit-content', maxWidth: '786px', borderRadius: '24px', border: '1px solid #E8E8EA' } }}>
            <DialogTitle sx={{ mb: "24px" }} p={{ xs: "16px 16px 15px !important" }} borderBottom="1px solid #E8EAF0">
                <Typography color='#333333' fontSize={{ xs: "22px", sm: "24px" }} fontWeight='500' >Buy Add-On</Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={() => ({
                        position: 'absolute',
                        right: { xs: "17px" },
                        top: { xs: "15px" },
                    })}
                    disabled={isProcessing}
                >
                    <ModalClose></ModalClose>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "0 16px 16px", sm: "0 32px 24px" }, position: 'relative' }}>
                {isProcessing && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                <Box>
                    {availableAddons.length === 0 ? (
                        <Box py={4} textAlign="center">
                            <Typography variant="body1" color="#666">
                                You have subscribed to all available add-ons.
                            </Typography>
                        </Box>
                    ) : (
                        <Box display="flex" gap="20px" flexWrap="wrap">
                            {availableAddons.map((addon) => (
                                <Paper
                                    key={addon.id}
                                    sx={{
                                        maxWidth: "353px",
                                        width: "100%",
                                        minHeight: "280px",
                                        p: "24px 15px",
                                        borderRadius: "8px",
                                        border: "1px solid #E8EAF0",
                                        boxShadow: "none",
                                        '@media (min-width:768px)': {
                                            width: "47%",
                                            minWidth: "300px",
                                        },
                                        '@media (min-width:320px) and (max-width:600px)': {
                                            maxWidth: "100%",
                                        },
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                                        <Box mb="24px">
                                            <Box mb="24px" display="flex" gap="18px" alignItems="center">
                                                {addon.icon}
                                                <Typography fontSize="18px" fontWeight="500" color="#333333">{addon.title}</Typography>
                                            </Box>
                                            <Typography color="#333333" fontSize="13px" lineHeight="24px">{addon.description}</Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap="16px">
                                            <Typography display="flex" alignItems="end" fontSize="20px" color="#22B060" fontWeight="600">
                                                ${addon.price}<Typography color="#333333" fontSize="13px" ml="4px" mb="2px">/ month</Typography>
                                            </Typography>
                                            <Button
                                                variant='contained'
                                                size='small'
                                                sx={{ textTransform: "capitalize"}}
                                                onClick={() => handleAddToPlan(addon.id)}
                                                disabled={isProcessing || !subscriptionId}
                                            >
                                                Add to Plan
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default BuyAddOns;
